import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Tab,
  Tabs,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/CloseSharp";
import ShareIcon from "@mui/icons-material/ShareSharp";
import { useState, useMemo, useEffect } from "react";
import { OutlinedInput } from "@components/outlined-input";
import { OutlinedSelect } from "@components/outlined-select";
import { useCreateSiteUser, type CreateSiteUserPayload } from "@hooks/use-create/user";
import { useUpdateUser } from "@hooks/use-update/user";
import { USER_FIELD_KEYS, type SiteUser } from "@hooks/use-get/users/types";
import { useUserConfigContext } from "@hooks/use-config-context/user";
import type { UserFieldConfig } from "@hooks/use-config/user";
import { AssetAssignmentHistoryTab } from "@views/asset-table/add-asset-modal/asset-assignment-history-tab";
import { useCheckUniqueFields, getUniquenessHelperText, type UniqueCheckField } from "@hooks/use-check-unique";
import { OutlinedDatePicker } from "@components/date-picker";
import { useCapabilities } from "@hooks/use-capabilities";
import { ToolTipIconButton } from "@components/tool-tip-icon-button";
import { copyModalLinkToClipboard } from "@views/asset-table/helperFunctions";
import {
  fixedModalPaperSx,
  modalHeaderActionsSx,
  modalTabsContainerSx,
  modalTabsSx,
  modalTitleSx,
  pinnedDialogActionsSx,
  scrollableModalDialogContentSx,
} from "@utils/modal-styles";

interface AddUserModalProps {
  open: boolean;
  onClose: () => void;
  editData?: SiteUser | null;
  onSuccess?: () => void;
}

type FormState = Record<string, string | number | null>;

const editIdentityFieldKeys = [
  USER_FIELD_KEYS.STUDENT_ID,
  USER_FIELD_KEYS.FIRST_NAME,
  USER_FIELD_KEYS.LAST_NAME,
] as const;

const identityFieldLabels: Record<(typeof editIdentityFieldKeys)[number], string> = {
  [USER_FIELD_KEYS.STUDENT_ID]: "Student ID",
  [USER_FIELD_KEYS.FIRST_NAME]: "First Name",
  [USER_FIELD_KEYS.LAST_NAME]: "Last Name",
};

// Maps standard WP user field keys to their check-unique source/field params
const USER_WP_FIELD_MAP: Record<string, string> = {
  [USER_FIELD_KEYS.USER_EMAIL]: "user_email",
  [USER_FIELD_KEYS.USER_LOGIN]: "user_login",
};

// Fallback for when the backend config omits field.required
const FALLBACK_REQUIRED_KEYS = new Set([
  USER_FIELD_KEYS.USER_EMAIL,
  USER_FIELD_KEYS.FIRST_NAME,
  USER_FIELD_KEYS.LAST_NAME,
  USER_FIELD_KEYS.ROLE,
]);

const buildForm = (config: Record<string, UserFieldConfig>, source?: SiteUser | null): FormState => {
  const form: FormState = { password: "" };
  Object.entries(config).forEach(([key, field]) => {
    if (!field.edit) return;
    const raw = source ? (source as any)[key] : undefined;
    form[key] = raw != null ? String(raw) : "";
  });
  return form;
};

export const AddUserModal = ({ open, onClose, editData, onSuccess }: AddUserModalProps) => {
  const isEditing = !!editData;
  const { config: userConfig } = useUserConfigContext();
  const { can } = useCapabilities();
  const canEdit = can("edit_users");
  const [openTabId, setOpenTabId] = useState(0);
  const [linkAlertOpen, setLinkAlertOpen] = useState(false);
  const [alertMsg, setAlertMsg] = useState("");

  const formSnapshot = useMemo(
    () => buildForm(userConfig || {}, editData),
    [userConfig, editData]
  );

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  const { createSiteUser, loading: createLoading, error: createError, clearError: clearCreateError } = useCreateSiteUser();
  const { updateUser, loading: updateLoading, error: updateError, clearError: clearUpdateError } = useUpdateUser();

  const loading = submitting || createLoading || updateLoading;

  const [formState, setFormState] = useState<FormState>(formSnapshot);

  useEffect(() => {
    if (!open) return;
    setFormState(formSnapshot);
    setErrors({});
    clearCreateError();
    clearUpdateError();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, formSnapshot]);

  const set = (field: string) => (value: string) =>
    setFormState((prev) => ({ ...prev, [field]: value }));

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    Object.entries(userConfig || {}).forEach(([key, field]) => {
      const isRequired = field.required !== undefined ? field.required : FALLBACK_REQUIRED_KEYS.has(key as any);
      if (isRequired && !String(formState[key] ?? "").trim()) {
        next[key] = `${field.name} is required.`;
      }
    });
    if (!isEditing && !String(formState.password ?? "").trim()) {
      next.password = "Password is required.";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);

    try {
      if (isEditing) {
        const updatePayload: FormState = {};
        Object.entries(userConfig || {}).forEach(([key, field]) => {
          if (!field.edit || key === USER_FIELD_KEYS.USER_EMAIL) return;
          const value = formState[key];
          updatePayload[key] = key === USER_FIELD_KEYS.GRADE
            ? (value !== "" ? Number(value) : null)
            : value || null;
        });

        const success = await updateUser(editData![USER_FIELD_KEYS.WP_USER_ID], updatePayload);
        if (success) {
          onSuccess?.();
        }
      } else {
        const email = String(formState[USER_FIELD_KEYS.USER_EMAIL]).trim();
        const createPayload: CreateSiteUserPayload = { user_email: email };

        if (formState.password) {
          createPayload.password = String(formState.password);
        }

        Object.entries(userConfig || {}).forEach(([key, field]) => {
          if (!field.edit || key === USER_FIELD_KEYS.USER_EMAIL || (formState[key] ?? "") === "") return;
          if (key === USER_FIELD_KEYS.GRADE) {
            (createPayload as any)[key] = Number(formState[key]);
          } else {
            (createPayload as any)[key] = formState[key];
          }
        });

        const success = await createSiteUser(createPayload);
        if (success) {
          onSuccess?.();
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormState(buildForm(userConfig || {}));
    setErrors({});
    setOpenTabId(0);
    onClose();
  };

  // Field order follows API config, but in edit mode always includes core identity fields.
  const visibleFields = useMemo(() => {
    if (!userConfig) return [];

    const configEntries = Object.entries(userConfig);
    const fallbackLabels: Record<string, string> = {
      [USER_FIELD_KEYS.STUDENT_ID]: "Student ID",
      [USER_FIELD_KEYS.FIRST_NAME]: "First Name",
      [USER_FIELD_KEYS.LAST_NAME]: "Last Name",
    };

    const identityEntries = isEditing
      ? editIdentityFieldKeys.map((key) => {
          const configuredField = userConfig[key];
          const field: UserFieldConfig = configuredField ?? {
            name: fallbackLabels[key],
            type: "text",
            editable_roles: [],
            edit: false,
            group: false,
            filter: false,
            options: [],
          };

          return {
            key,
            field,
            isEditable: !!configuredField?.edit,
          };
        })
      : [];

    if (!isEditing) {
      return configEntries
        .filter(([, field]) => field.edit)
        .map(([key, field]) => ({ key, field, isEditable: true }));
    }

    const editableEntries = configEntries
      .filter(([key, field]) => field.edit && !editIdentityFieldKeys.includes(key as any))
      .map(([key, field]) => ({ key, field, isEditable: true }));

    return [...identityEntries, ...editableEntries];
  }, [userConfig, isEditing]);

  // Build uniqueFields from every config field that has unique: true.
  // In edit mode, skip fields whose value hasn't changed from the saved record.
  const uniqueFieldValues = useMemo(() => {
    const result: Record<string, UniqueCheckField> = {};
    Object.entries(userConfig || {}).forEach(([key, field]) => {
      if (!field.unique) return;
      const currentVal = String(formState[key] ?? "");
      if (isEditing) {
        const originalVal = String(editData?.[key as keyof SiteUser] ?? "");
        if (!currentVal || currentVal === originalVal) return;
      }
      const wpField = USER_WP_FIELD_MAP[key];
      result[key] = wpField
        ? { value: currentVal, source: "wp_users", field: wpField }
        : { value: currentVal, source: "ue_fields", field: key };
    });
    return result;
  }, [userConfig, isEditing, formState, editData]);

  const uniqueChecks = useCheckUniqueFields({
    type: "user",
    uniqueFields: uniqueFieldValues,
    excludeId: isEditing ? editData?.[USER_FIELD_KEYS.WP_USER_ID] : undefined,
  });

  const guaranteedIdentityFields = useMemo(() => {
    if (!isEditing || !editData) return [] as Array<{ key: string; label: string; value: string }>;

    const visibleFieldKeys = new Set(visibleFields.map((field) => field.key));

    return editIdentityFieldKeys
      .filter((key) => !visibleFieldKeys.has(key))
      .map((key) => ({
        key,
        label: identityFieldLabels[key],
        value: String(formState[key] ?? editData[key as keyof SiteUser] ?? ""),
      }));
  }, [isEditing, editData, visibleFields, formState]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: fixedModalPaperSx }}
    >
      <DialogTitle sx={modalTitleSx}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={1}
        >
          <Typography variant="h6" mb={1}>
            {isEditing ? (canEdit ? "Edit User" : "View User") : "Add User"}
          </Typography>
          <Stack direction="row" spacing={1} sx={modalHeaderActionsSx}>
            {isEditing && editData?.[USER_FIELD_KEYS.WP_USER_ID] && can("list_users") && (
              <ToolTipIconButton
                text="Share User"
                icon={<ShareIcon />}
                onClick={() =>
                  copyModalLinkToClipboard(
                    editData[USER_FIELD_KEYS.WP_USER_ID],
                    "users",
                    setLinkAlertOpen,
                    setAlertMsg
                  )
                }
              />
            )}
            <ToolTipIconButton
              text="Close"
              icon={<CloseIcon />}
              onClick={() => onClose()}
            />
          </Stack>
        </Stack>
        {linkAlertOpen && (
          <Alert
            severity="info"
            onClose={() => { setLinkAlertOpen(false); setAlertMsg(""); }}
            sx={{ mb: 1 }}
          >
            {alertMsg}
          </Alert>
        )}
        {(createError || updateError) && !loading && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {createError || updateError}
          </Alert>
        )}
        <Stack direction="row" spacing={{ xs: 1, sm: 2 }} mb={isEditing ? 1 : 0}>
          {isEditing && (
            <Box
              sx={modalTabsContainerSx}
            >
              <Tabs
                value={openTabId}
                onChange={(_, v) => setOpenTabId(v)}
                textColor="primary"
                indicatorColor="primary"
                aria-label="user modal tabs"
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={modalTabsSx}
              >
                <Tab label="Details" value={0} />
                <Tab label="Assignment History" value={1} />
              </Tabs>
            </Box>
          )}
        </Stack>
      </DialogTitle>

      <Divider />

      <DialogContent sx={scrollableModalDialogContentSx}>
        {openTabId === 0 && (
          <Stack spacing={2} sx={{ mt: 1 }}>
          {guaranteedIdentityFields.map(({ key, label, value }) => (
            <OutlinedInput
              key={key}
              label={label}
              value={value}
              onChange={() => {}}
              disabled
            />
          ))}

          {visibleFields.map(({ key, field, isEditable }) => (
            <div key={key}>
              {field.type === "select" ? (
                <OutlinedSelect
                  label={field.name}
                  value={String(formState[key] ?? editData?.[key as keyof SiteUser] ?? "")}
                  onChange={set(key)}
                  disabled={(isEditing && (!canEdit || !isEditable))}
                  error={errors[key]}
                  required={!!field.required || FALLBACK_REQUIRED_KEYS.has(key as any)}
                  forceValidate={!!errors[key]}
                  options={field.options.length > 0
                    ? [{ value: "", label: "— None —" }, ...field.options]
                    : [{ value: "", label: "— None —" }]
                  }
                />
              ) : field.type === "date" ? (
                <OutlinedDatePicker
                  label={field.name}
                  value={String(formState[key] ?? "") || null}
                  onChange={(v) => set(key)(v ?? "")}
                  disabled={(isEditing && (!canEdit || !isEditable))}
                  error={errors[key]}
                />
              ) : (
                <OutlinedInput
                  label={field.name}
                  value={String(formState[key] ?? editData?.[key as keyof SiteUser] ?? "")}
                  onChange={set(key)}
                  error={errors[key]}
                  required={!!field.required || FALLBACK_REQUIRED_KEYS.has(key as any)}
                  forceValidate={!!errors[key]}
                  disabled={(key === USER_FIELD_KEYS.USER_EMAIL && isEditing) || (isEditing && (!canEdit || !isEditable))}
                  helperText={
                    field.unique
                      ? getUniquenessHelperText(
                          uniqueChecks[key]?.isChecking ?? false,
                          uniqueChecks[key]?.isUnique ?? null,
                          String(formState[key] ?? ""),
                        )
                      : undefined
                  }
                />
              )}
            </div>
          ))}
          {!isEditing && (
            <OutlinedInput
              label="Password"
              value={String(formState.password ?? "")}
              onChange={set("password")}
              error={errors.password}
              forceValidate={!!errors.password}
              required
              type="password"
            />
          )}
        </Stack>
        )}

        {openTabId === 1 && isEditing && editData && (
          <AssetAssignmentHistoryTab wpUserId={editData[USER_FIELD_KEYS.WP_USER_ID]} />
        )}
      </DialogContent>

      <DialogActions sx={pinnedDialogActionsSx}>
        {openTabId === 0 ? (
          <>
            <Button onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            {(!isEditing || canEdit) && (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loading || Object.values(uniqueChecks).some((c) => c.isUnique === false)}
                startIcon={loading ? <CircularProgress size={16} /> : undefined}
              >
                {isEditing ? "Save" : "Create"}
              </Button>
            )}
          </>
        ) : (
          <Button onClick={handleClose}>Close</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};


